import { useCallback, useEffect, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { tr } from "./i18n.js";

/**
 * Punțile către alte aplicații, și maparea obiectivelor lor.
 *
 * ECRANUL ĂSTA E CERUT DE CONTRACT. `docs/Contract_Sesizari_Tehnice.md` §6
 * spune, despre o referință care sună și nu e recunoscută: „Nu se aruncă în
 * tăcere. Firma trebuie să vadă în ecran: «un obiectiv trimite sesizări și nu
 * l-ai mapat încă», cu un buton de mapare de acolo. Altfel prima instalare
 * arată identic cu una stricată: nu se întâmplă nimic și nimeni nu știe de ce."
 *
 * De-aia lista celor nemapate stă PRIMA, nu într-o filă lăturalnică: e singurul
 * loc din aplicație în care se vede că cineva bate la ușă și n-a fost primit.
 * Când e goală nu spune „nimic de făcut", ci că toate obiectivele care sună
 * sunt cunoscute — două lucruri foarte diferite.
 */

const NUME_FEL = {
  echipament_offline: "echipament offline",
  pierdere_comunicatie: "pierdere de comunicație",
  alimentare: "alimentare",
  sabotaj: "sabotaj",
  defect_functional: "defect funcțional",
  altul: "altceva",
};

const cand = (d) => (d ? new Date(d).toLocaleString("ro-RO") : "—");

export default function Punti({ eu }) {
  const [chei, setChei] = useState([]);
  const [mapari, setMapari] = useState([]);
  const [nemapate, setNemapate] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [locatii, setLocatii] = useState([]);
  const [incarca, setIncarca] = useState(true);
  const [eroare, setEroare] = useState("");
  const [mesaj, setMesaj] = useState("");
  const [cheieNoua, setCheieNoua] = useState(null);
  const [numeCheie, setNumeCheie] = useState("");
  const [form, setForm] = useState(null);

  // Butoanele se citesc din MATRICE, nu dintr-o listă de roluri scrisă aici.
  // Dacă cererea pică, rămâne valoarea de pornire — un ecran care nu știe ce
  // drepturi ai arată mai puțin, nu mai mult.
  const [poateAdministra, setPoateAdministra] = useState(eu.rol === "ADMIN_TENANT");

  const reincarca = useCallback(async () => {
    setIncarca(true);
    try {
      const [k, m, n] = await Promise.all([
        api.integrariChei().catch(() => []),
        api.integrariMapari().catch(() => []),
        api.integrariNemapate(),
      ]);
      setChei(Array.isArray(k) ? k : []);
      setMapari(Array.isArray(m) ? m : []);
      setNemapate(Array.isArray(n) ? n : []);
      setEroare("");
    } catch (e) {
      setEroare(mesajEroare(e));
    } finally {
      setIncarca(false);
    }
  }, []);

  useEffect(() => { reincarca(); }, [reincarca]);
  useEffect(() => {
    api.drepturileMele()
      .then((d) => setPoateAdministra((d?.integrari ?? []).includes("administrare")))
      .catch(() => {});
  }, []);

  // Clienții se aduc abia când se deschide primul formular: la încărcarea
  // ecranului n-au niciun rost, iar lista poate fi lungă.
  async function deschideForm(preumplut) {
    setMesaj(""); setEroare("");
    if (!clienti.length) {
      try { setClienti((await api.clienti()) ?? []); } catch (e) { setEroare(mesajEroare(e)); }
    }
    setLocatii([]);
    setForm({
      cheieApiId: preumplut?.cheieApiId ?? chei.find((c) => c.activa)?.id ?? "",
      referintaObiectiv: preumplut?.referintaObiectiv ?? "",
      referintaEchipament: preumplut?.referintaEchipament ?? "",
      clientId: "", locatieId: "", note: "",
    });
  }

  async function alegeClient(clientId) {
    setForm((f) => ({ ...f, clientId, locatieId: "" }));
    if (!clientId) { setLocatii([]); return; }
    try { setLocatii((await api.locatii(clientId)) ?? []); } catch { setLocatii([]); }
  }

  async function fa(actiune, dupa) {
    setEroare(""); setMesaj("");
    try {
      const r = await actiune();
      if (dupa) dupa(r);
      await reincarca();
    } catch (e) {
      setEroare(mesajEroare(e));
    }
  }

  /*
   * Două liste, nu una.
   *
   * Un obiectiv nemapat înseamnă sesizări PIERDUTE — nu s-a deschis niciun
   * tichet. Un echipament nemapat pe un obiectiv mapat înseamnă doar că
   * tichetul s-a deschis pe locație, nu pe echipament: nimic nu s-a pierdut,
   * se poate doar coborî maparea. Amestecate, primele s-ar îneca printre
   * celelalte — și tocmai ele cer o acțiune azi.
   */
  const nerezolvate = nemapate.filter((n) => !n.rezolvataLa);
  const refuzate = nerezolvate.filter((n) => n.refuzata);
  const rafinari = nerezolvate.filter((n) => !n.refuzata);
  const gata = form
    && form.cheieApiId && form.referintaObiectiv.trim() && form.clientId && form.locatieId;

  /** Aceleași coloane pentru ambele liste — o a doua copie ar începe să difere. */
  const tabel = (randuri) => (
    <div className="tabel-derulabil">
      <table className="tabel">
        <thead>
          <tr>
            <th>{tr("Obiectiv")}</th>
            <th>{tr("Echipament")}</th>
            <th>{tr("Punte")}</th>
            <th>{tr("Ultimul defect")}</th>
            <th>{tr("De câte ori")}</th>
            <th>{tr("Ultima dată")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {randuri.map((n) => (
            <tr key={n.id}>
              <td>
                <strong>{n.referintaObiectiv}</strong>
                {n.denumireObiectiv && <div className="subtitlu">{n.denumireObiectiv}</div>}
              </td>
              <td>
                {n.referintaEchipament ?? "—"}
                {n.denumireEchipament && <div className="subtitlu">{n.denumireEchipament}</div>}
              </td>
              <td>
                {n.cheieApi?.nume ?? "—"}
                {n.instanta && <div className="subtitlu">{n.instanta}</div>}
              </td>
              <td>{tr(NUME_FEL[n.ultimulFel] ?? "altceva")}</td>
              <td>{n.deCateOri}</td>
              <td>{cand(n.ultimaLa)}</td>
              <td>
                {poateAdministra && (
                  <button
                    className="buton buton-mic"
                    onClick={() => deschideForm({
                      cheieApiId: n.cheieApiId,
                      referintaObiectiv: n.referintaObiectiv,
                      referintaEchipament: n.referintaEchipament ?? "",
                    })}
                  >
                    {tr("Mapează")}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="continut">
      <h2>{tr("Punți și mapări")}</h2>
      <p className="subtitlu">
        {tr("O punte lasă altă aplicație — dispeceratul, de pildă — să deschidă tichete aici, cu secretul ei. Ea trimite referințele EI; legătura cu clienții și locațiile noastre o ținem tot noi, mai jos.")}
      </p>

      {eroare && <div className="eroare" role="alert">{eroare}</div>}
      {mesaj && <div className="mesaj">{mesaj}</div>}
      {incarca && <div className="gol">{tr("Se încarcă…")}</div>}

      {/* ── Cine sună și n-a fost recunoscut ─────────────────────────────── */}
      <div className="panou">
        <div className="panou-antet">
          {tr("Obiective care sună și nu sunt mapate")}
          {refuzate.length > 0 && <span className="pastila pastila-rau"> {refuzate.length}</span>}
        </div>
        <div className="panou-corp">
          {refuzate.length === 0 ? (
            <div className="gol">
              {tr("Niciunul. Toate obiectivele care au trimis sesizări sunt cunoscute.")}
            </div>
          ) : (
            <>
              <p className="subtitlu">
                {tr("Sesizările astea au fost REFUZATE — nu știm pe cine reprezintă. Până le mapezi, defecțiunile lor nu ajung la nimeni.")}
              </p>
              {tabel(refuzate)}
            </>
          )}
        </div>
      </div>

      {/* ── Ce merge, dar s-ar putea și mai precis ────────────────────────── */}
      {rafinari.length > 0 && (
        <div className="panou">
          <div className="panou-antet">
            {tr("Echipamente care s-ar putea mapa mai precis")}
            <span className="pastila"> {rafinari.length}</span>
          </div>
          <div className="panou-corp">
            <p className="subtitlu">
              {tr("Aici NU s-a pierdut nimic: obiectivul e mapat, tichetele s-au deschis pe locație. Mapând și echipamentul, tehnicianul ar ști de la început ce anume s-a defectat.")}
            </p>
            {tabel(rafinari)}
          </div>
        </div>
      )}

      {/* ── Maparea ───────────────────────────────────────────────────────── */}
      <div className="panou">
        <div className="panou-antet">{tr("Maparea obiectivelor")}</div>
        <div className="panou-corp">
          {poateAdministra && !form && (
            <button className="buton buton-mic" onClick={() => deschideForm()}>
              {tr("Mapează un obiectiv")}
            </button>
          )}

          {form && (
            <>
              <div className="formular-linie">
                <div className="camp">
                  <label htmlFor="p-punte">{tr("Puntea")}</label>
                  <select
                    id="p-punte"
                    value={form.cheieApiId}
                    onChange={(e) => setForm({ ...form, cheieApiId: e.target.value })}
                  >
                    <option value="">{tr("— alege —")}</option>
                    {chei.filter((c) => c.activa).map((c) => (
                      <option key={c.id} value={c.id}>{c.nume}</option>
                    ))}
                  </select>
                </div>
                <div className="camp">
                  <label htmlFor="p-obi">{tr("Referința obiectivului, la ei")}</label>
                  <input
                    id="p-obi"
                    value={form.referintaObiectiv}
                    onChange={(e) => setForm({ ...form, referintaObiectiv: e.target.value })}
                    placeholder="4412"
                  />
                </div>
                <div className="camp">
                  <label htmlFor="p-ech">{tr("Referința echipamentului (opțional)")}</label>
                  <input
                    id="p-ech"
                    value={form.referintaEchipament}
                    onChange={(e) => setForm({ ...form, referintaEchipament: e.target.value })}
                    placeholder="CAM-07"
                  />
                </div>
              </div>
              <p className="subtitlu">
                {tr("Lasă referința echipamentului goală ca să mapezi obiectivul întreg: atunci tichetele se deschid pe locație.")}
              </p>
              <div className="formular-linie">
                <div className="camp">
                  <label htmlFor="p-client">{tr("Clientul nostru")}</label>
                  <select id="p-client" value={form.clientId} onChange={(e) => alegeClient(e.target.value)}>
                    <option value="">{tr("— alege —")}</option>
                    {clienti.map((c) => (
                      <option key={c.id} value={c.id}>{c.denumire}</option>
                    ))}
                  </select>
                </div>
                <div className="camp">
                  <label htmlFor="p-locatie">{tr("Locația")}</label>
                  <select
                    id="p-locatie"
                    value={form.locatieId}
                    onChange={(e) => setForm({ ...form, locatieId: e.target.value })}
                    disabled={!form.clientId}
                  >
                    <option value="">{tr("— alege —")}</option>
                    {locatii.map((l) => (
                      <option key={l.id} value={l.id}>{l.denumire}</option>
                    ))}
                  </select>
                </div>
                <div className="camp">
                  <label htmlFor="p-nota">{tr("Notă (opțional)")}</label>
                  <input
                    id="p-nota"
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                  />
                </div>
              </div>
              <button
                className="buton buton-mic"
                disabled={!gata}
                onClick={() => fa(
                  () => api.integrariCreeazaMapare({
                    cheieApiId: form.cheieApiId,
                    referintaObiectiv: form.referintaObiectiv.trim(),
                    referintaEchipament: form.referintaEchipament.trim() || undefined,
                    clientId: form.clientId,
                    locatieId: form.locatieId,
                    note: form.note.trim() || undefined,
                  }),
                  () => { setForm(null); setMesaj(tr("Maparea s-a făcut.")); },
                )}
              >
                {tr("Salvează maparea")}
              </button>
              <button className="buton buton-mic buton-discret" onClick={() => setForm(null)}>
                {tr("Renunț")}
              </button>
            </>
          )}

          {mapari.length === 0 ? (
            <div className="gol">{tr("Nicio mapare încă.")}</div>
          ) : (
            <div className="tabel-derulabil">
              <table className="tabel">
                <thead>
                  <tr>
                    <th>{tr("Obiectiv")}</th>
                    <th>{tr("Echipament")}</th>
                    <th>{tr("Punte")}</th>
                    <th>{tr("Client")}</th>
                    <th>{tr("Locație")}</th>
                    <th>{tr("Pusă la")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {mapari.map((m) => (
                    <tr key={m.id}>
                      <td><strong>{m.referintaObiectiv}</strong></td>
                      <td>{m.referintaEchipament ?? tr("— tot obiectivul —")}</td>
                      <td>{m.cheieApi?.nume ?? "—"}</td>
                      <td>{m.client?.denumire ?? "—"}</td>
                      <td>{m.locatie?.denumire ?? "—"}</td>
                      <td>{cand(m.creataLa)}</td>
                      <td>
                        {poateAdministra && (
                          <button
                            className="buton buton-mic buton-sters"
                            onClick={() => fa(
                              () => api.integrariStergeMapare(m.id),
                              () => setMesaj(tr("Maparea s-a șters. Obiectivul va suna din nou în gol.")),
                            )}
                          >
                            {tr("Șterge")}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Cheile ────────────────────────────────────────────────────────── */}
      <div className="panou">
        <div className="panou-antet">{tr("Punțile pornite")}</div>
        <div className="panou-corp">
          {cheieNoua && (
            <div className="panou-atentie">
              <strong>{tr("Copiază secretul acum — nu se mai arată niciodată.")}</strong>
              <div><code className="cod-mic">{cheieNoua}</code></div>
              <button className="buton buton-mic buton-discret" onClick={() => setCheieNoua(null)}>
                {tr("L-am copiat")}
              </button>
            </div>
          )}
          {poateAdministra && (
            <div className="formular-linie">
              <div className="camp">
                <label htmlFor="p-nume">{tr("Numele punții")}</label>
                <input
                  id="p-nume"
                  value={numeCheie}
                  onChange={(e) => setNumeCheie(e.target.value)}
                  placeholder={tr("Dispecerat Central")}
                />
              </div>
              <div className="camp">
                <label>&nbsp;</label>
                <button
                  className="buton buton-mic"
                  disabled={numeCheie.trim().length < 3}
                  onClick={() => fa(
                    () => api.integrariCreeazaCheie({ nume: numeCheie.trim() }),
                    (r) => { setCheieNoua(r?.cheie ?? ""); setNumeCheie(""); },
                  )}
                >
                  {tr("Pornește o punte")}
                </button>
              </div>
            </div>
          )}
          {chei.length === 0 ? (
            <div className="gol">{tr("Nicio punte pornită.")}</div>
          ) : (
            <div className="tabel-derulabil">
              <table className="tabel">
                <thead>
                  <tr>
                    <th>{tr("Nume")}</th>
                    <th>{tr("Prefix")}</th>
                    <th>{tr("Stare")}</th>
                    <th>{tr("Ultima folosire")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {chei.map((c) => (
                    <tr key={c.id}>
                      <td>{c.nume}</td>
                      <td><code className="cod-mic">{c.prefix}</code></td>
                      <td>
                        <span className={c.activa ? "pastila pastila-bine" : "pastila"}>
                          {c.activa ? tr("pornită") : tr("revocată")}
                        </span>
                      </td>
                      <td>{cand(c.ultimaFolosireLa)}</td>
                      <td>
                        {poateAdministra && c.activa && (
                          <button
                            className="buton buton-mic buton-sters"
                            onClick={() => fa(
                              () => api.integrariRevocaCheie(c.id),
                              () => setMesaj(tr("Puntea s-a oprit. Sesizările ei nu mai intră.")),
                            )}
                          >
                            {tr("Revocă")}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
